import { useEffect, useState } from "react";
import { Select } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";
import type { LabelBackground, LabelType } from "../../api/types";

interface Props {
  labelType: LabelType;
  value: string;
  onChange: (value: string) => void;
}

const LS_PREFIX = "labelBg.";

export function BackgroundPicker({ labelType, value, onChange }: Props) {
  const { t } = useTranslation();
  const [backgrounds, setBackgrounds] = useState<LabelBackground[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);

  useEffect(() => {
    api.listLabelBackgrounds(labelType).then((r) => {
      setBackgrounds(r.backgrounds);
      setDefaultId(r.default_background_id);
    }).catch(() => {});
  }, [labelType]);

  const data = [
    {
      value: "default",
      label: defaultId
        ? t("labelEditor.pickDefault", {
            name: backgrounds.find((b) => b.id === defaultId)?.name ?? "—",
          })
        : t("labelEditor.pickNoDefault"),
    },
    { value: "none", label: t("labelEditor.pickNone") },
    ...backgrounds.map((b) => ({ value: b.id, label: b.name })),
  ];

  function handleChange(v: string | null) {
    if (!v) return;
    onChange(v);
    try {
      localStorage.setItem(LS_PREFIX + labelType, v);
    } catch { /* quota */ }
  }

  return (
    <Select
      size="xs"
      data={data}
      value={value}
      onChange={handleChange}
      label={t("labelEditor.pickBackground")}
      allowDeselect={false}
      w={240}
    />
  );
}

export function loadStoredBackground(labelType: LabelType): string {
  try {
    return localStorage.getItem(LS_PREFIX + labelType) || "default";
  } catch {
    return "default";
  }
}
